import { FIXED_HEADERS, getEndpoint, HEADER_PREFIX, MAX_HEADER_BYTES } from './config';
import type { EventIdentity, TrackedEvent } from './types';

// Single module-scope encoder (stateless) avoids re-allocating per send.
const encoder = new TextEncoder();

const byteLength = (value: string): number => encoder.encode(value).length;

// An out-of-range header value or a non-token header name makes `new Headers()`
// (inside fetch) throw SYNCHRONOUSLY — before any promise, so the fetch `.catch`
// can't intercept it. buildHeaders legalises values and drops illegal-named keys
// to prevent that throw; sendEvent's try/catch is the backstop.
const isHeaderValueLegal = (value: string): boolean => {
    for (let i = 0; i < value.length; i++) {
        const code = value.charCodeAt(i);
        if (code === 0x09) continue; // HTAB is the one legal control char
        if (code < 0x20 || code === 0x7f || code > 0xff) return false; // ctrl / DEL / >ISO-8859-1
    }
    return true;
};

const ILLEGAL_HEADER_NAME = /[^!#$%&'*+\-.^_`|~0-9A-Za-z]/; // RFC 7230 token complement

// A payload key whose derived name equals a fixed header (e.g. 'Event-Type' ->
// 'X-SB-Event-Type') would silently overwrite the real identity/event value —
// and, if largest, could be dropped in the overflow loop, deleting the fixed
// header entirely. Drop such keys the same way illegal-named keys are dropped.
const FIXED_NAMES = new Set<string>(Object.values(FIXED_HEADERS));

// Legal values (ASCII, ISO-8859-1, JSON.stringify output) pass verbatim so the
// sink round-trip is unchanged; anything else is percent-encoded (decodable at
// the sink via decodeURIComponent) so non-ASCII payloads survive losslessly.
const legaliseHeaderValue = (value: string): string => (isHeaderValueLegal(value) ? value : encodeURIComponent(value));

// Header names follow the SB-6468 contract: Title-kebab (`X-SB-Typed-Text`),
// matching the fixed headers. A raw snake_case key would emit `X-SB-typed_text`,
// which underscore-stripping proxies (nginx default `underscores_in_headers off`)
// silently drop — so the payload key is converted here, not passed through.
const toTitleKebab = (key: string): string =>
    key
        .split(/[_-]+/)
        .filter((part) => part.length > 0)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join('-');

/**
 * Assemble the `X-SB-*` header map for one event.
 *
 * Fixed identity/event headers come first and are never drop-eligible.
 * `X-SB-User-Id` / `X-SB-Page-Type` / `X-SB-Device` are omitted when their source
 * is null (the sink reads absence as guest / not-applicable). Each payload key
 * becomes one `X-SB-<key>` header (objects JSON.stringify'd). On overflow past
 * MAX_HEADER_BYTES, payload headers are dropped largest-first (never truncated —
 * truncated JSON is unparseable at the sink; a dropped field is a clean gap).
 */
const buildHeaders = (event: TrackedEvent, identity: EventIdentity): Record<string, string> => {
    // Fixed values are caller-supplied too, so legalise them as well.
    const headers: Record<string, string> = {
        [FIXED_HEADERS.SESSION_ID]: legaliseHeaderValue(identity.session_id),
        [FIXED_HEADERS.EVENT_TYPE]: legaliseHeaderValue(event.event_type),
        [FIXED_HEADERS.EVENT_TIMESTAMP]: String(event.timestamp),
    };

    if (identity.user_id != null) {
        headers[FIXED_HEADERS.USER_ID] = legaliseHeaderValue(identity.user_id);
    }
    if (event.device != null) {
        headers[FIXED_HEADERS.DEVICE] = legaliseHeaderValue(event.device);
    }
    if (event.page.page_type != null) {
        headers[FIXED_HEADERS.PAGE_TYPE] = legaliseHeaderValue(event.page.page_type);
    }

    // Payload headers are the only drop-eligible set; track sizes for overflow.
    const payloadHeaders: { name: string; size: number }[] = [];
    const illegalKeys: string[] = [];
    for (const [key, value] of Object.entries(event.payload)) {
        const name = `${HEADER_PREFIX}${toTitleKebab(key)}`;
        // Drop (don't throw on) a key whose name isn't a legal header token.
        if (ILLEGAL_HEADER_NAME.test(name)) {
            illegalKeys.push(key);
            continue;
        }
        // A key colliding with a fixed header name must not clobber the real
        // identity/event value; drop it like an illegal-named key.
        if (FIXED_NAMES.has(name)) {
            illegalKeys.push(key);
            continue;
        }
        const raw = typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value);
        const encoded = legaliseHeaderValue(raw);
        headers[name] = encoded;
        payloadHeaders.push({ name, size: byteLength(name) + byteLength(encoded) });
    }
    if (illegalKeys.length > 0) {
        console.error('sbTracker: dropped payload field(s) with illegal header name', illegalKeys);
    }

    let total = Object.entries(headers).reduce((sum, [name, value]) => sum + byteLength(name) + byteLength(value), 0);
    if (total <= MAX_HEADER_BYTES) {
        return headers;
    }

    // Drop payload headers largest-first until under the cap (fixed headers are
    // never in payloadHeaders).
    const dropped: string[] = [];
    payloadHeaders.sort((a, b) => b.size - a.size);
    for (const { name, size } of payloadHeaders) {
        if (total <= MAX_HEADER_BYTES) break;
        delete headers[name];
        dropped.push(name);
        total -= size;
    }
    console.error('sbTracker: header overflow — dropped field(s)', dropped);

    return headers;
};

/**
 * Send a single event, all fields as `X-SB-*` request headers with an empty body.
 * fetch is the only transport (it is the only API that can set request headers,
 * and no app-capable browser lacks it). When fetch is unavailable the send is
 * refused so the queue journals the event for a later retry.
 *
 * @returns whether the send was accepted (so the queue knows whether to journal).
 */
export const sendEvent = (event: TrackedEvent, identity: EventIdentity): boolean => {
    // Endpoint explicitly cleared via setEndpoint(''); no-op, don't journal.
    if (getEndpoint() === '') {
        return true;
    }

    if (typeof fetch !== 'function') {
        console.error('sbTracker: fetch unavailable — event journalled for retry');
        return false;
    }

    // Fire-and-forget: returns `true` before the promise settles, so a rejected
    // fetch loses the event. The try/catch is the backstop for a synchronous
    // Headers-construction throw (see isHeaderValueLegal) that `.catch` can't see —
    // return false so the event journals instead of the exception reaching the
    // caller. body: '' is explicit (keeps $request_body deterministic; no body).
    try {
        fetch(getEndpoint(), {
            method: 'POST',
            keepalive: true,
            headers: buildHeaders(event, identity),
            body: '',
        }).catch((error) => {
            console.error('sbTracker: event send failed', error);
        });
    } catch (error) {
        console.error('sbTracker: event send threw synchronously', error);
        return false;
    }
    return true;
};
